/** @jsxImportSource @emotion/react */
/**
 * Module dependencies.
 */

import { InViewOnce } from '../in-view-once/in-view-once';
import { JSX } from 'react';
import { StateDelay } from 'components/providers/animation-delay-provider/animation-delay-provider';
import { Typography, TypographyProps, css } from '@mui/material';

/**
 * `Props` type.
 */

type Props = TypographyProps & { state: StateDelay };

/**
 * `TypographyComponent` component.
 */

function TypographyComponent({ children, state, ...rest }: Props): JSX.Element {
  const { delay, hasEntered } = state;

  if (typeof children !== 'string') {
    return <Typography {...rest}>{children}</Typography>;
  }

  const words = children.trim().split(' ');
  const delayStep = 15;

  return (
    <Typography {...(rest as any)} component={'div'}>
      {words.map((word, i) => (
        <span
          css={css`
            display: inline-block;
            opacity: ${hasEntered ? 1 : 0};
            transform: translateY(${hasEntered ? '0' : '20px'});
            transition:
              opacity 0.5s ease,
              transform 0.5s ease;
            transition-delay: ${delay + i * delayStep}ms;
            margin-right: 6px;
            will-change: opacity, transform;
          `}
          key={i}
        >
          {word}
        </span>
      ))}
    </Typography>
  );
}

/**
 * Export `AnimatedTypography` component.
 */

export function AnimatedTypography(props: TypographyProps): JSX.Element {
  return (
    <InViewOnce>
      {(state) => <TypographyComponent {...props} state={state} />}
    </InViewOnce>
  );
}
